<script setup lang="ts">
import {
  EButton,
  EDropdownMenu,
  ETooltip,
  formatBinding,
  GLOBAL_SHORTCUTS,
  type MenuItem,
} from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import AppIcon from '@/components/AppIcon.vue'
import { QUICK_CREATE_ACTIONS, useQuickCreate, type QuickCreateTarget } from '@/composables/useQuickCreate'
import { useReadOnly } from '@/composables/useReadOnly'
import { useAuthStore } from '@/stores/auth'

/**
 * The "+" menu. Every entry opens a drawer over the current page rather than
 * navigating, so an approver halfway through a list never loses their place.
 */

const auth = useAuthStore()
const quickCreate = useQuickCreate()
const { readOnly, reason } = useReadOnly()
const { t } = useI18n()

const available = computed(() => QUICK_CREATE_ACTIONS.filter((action) => auth.can(action.permission)))

const items = computed<MenuItem[]>(() =>
  available.value.map((action) => ({ id: action.id, label: t(action.labelKey), icon: 'plus' })),
)

const hint = computed(() => formatBinding(GLOBAL_SHORTCUTS.quickCreate))

function onSelect(item: MenuItem): void {
  quickCreate.open(item.id as QuickCreateTarget)
}
</script>

<template>
  <EDropdownMenu
    v-if="available.length > 0 && !readOnly"
    :items="items"
    :label="$t('shell.quickCreate.menuLabel')"
    @select="onSelect"
  >
    <template #trigger>
      <span>
        <ETooltip :content="$t('shell.quickCreate.trigger')" :shortcut="hint">
          <EButton variant="primary" size="sm" icon-only :aria-label="$t('shell.quickCreate.trigger')">
            <template #icon><AppIcon name="plus" :size="15" /></template>
          </EButton>
        </ETooltip>
      </span>
    </template>
  </EDropdownMenu>

  <ETooltip v-else-if="available.length > 0" :content="reason">
    <EButton
      variant="primary"
      size="sm"
      icon-only
      disabled
      :aria-label="$t('shell.quickCreate.unavailable')"
    >
      <template #icon><AppIcon name="plus" :size="15" /></template>
    </EButton>
  </ETooltip>
</template>
